import React, { useState, useCallback } from 'react';
import { 
  List, 
  Table, 
  Edit3, 
  Trash2, 
  Copy, 
  GripVertical, 
  Plus,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { Button } from './ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.tsx';
import { cn } from '../lib/utils.ts';
import { StoryboardStep, Storyboard } from '../types/index.ts';

interface StoryboardEditorProps {
  storyboard: Storyboard;
  onStoryboardUpdate: (updatedStoryboard: Storyboard) => void;
  onStepEdit: (stepId: string) => void;
  isLoading?: boolean;
}

type ViewMode = 'simple' | 'advanced';

const StepCard: React.FC<{
  step: StoryboardStep;
  index: number;
  isSimpleMode: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onReorder: (direction: 'up' | 'down') => void;
}> = ({ step, index, isSimpleMode, onEdit, onDelete, onDuplicate, onReorder }) => {
  const getValidationColor = (validation: string) => {
    switch (validation) {
      case 'high':
      case 'excellent':
      case 'verified':
        return 'text-green-600 bg-green-100';
      case 'medium':
      case 'good':
      case 'warning':
        return 'text-yellow-600 bg-yellow-100';
      case 'low':
      case 'needs_improvement':
      case 'critical':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  if (isSimpleMode) {
    return (
      <Card className="mb-3">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center justify-center w-8 h-8 bg-primary text-primary-foreground rounded-full text-sm font-semibold">
                {index + 1}
              </div>
              <div className="flex flex-col gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onReorder('up')}
                  disabled={index === 0}
                  className="h-6 w-6 p-0"
                >
                  <GripVertical className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onReorder('down')}
                  className="h-6 w-6 p-0"
                >
                  <GripVertical className="h-3 w-3" />
                </Button>
              </div>
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h4 className="font-medium">{step.onscreenText}</h4>
                <span className={cn(
                  'text-xs px-2 py-1 rounded-full',
                  getValidationColor(step.validation.completeness)
                )}>
                  {step.validation.completeness}
                </span>
              </div>
              
              <p className="text-sm text-muted-foreground mb-2">
                {step.ttsText}
              </p>
              
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>Visual: {step.visualSuggestion}</span>
                <span>•</span>
                <span>Source: {step.sourceDocReference.section}</span>
              </div>
            </div>
            
            <div className="flex flex-col gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={onEdit}
                className="h-8 w-8 p-0"
              >
                <Edit3 className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onDuplicate}
                className="h-8 w-8 p-0"
              >
                <Copy className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onDelete}
                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <tr className="border-b">
      <td className="p-3 text-center font-medium">{index + 1}</td>
      <td className="p-3">
        <div className="max-w-xs">
          <input
            type="text"
            value={step.onscreenText}
            className="w-full p-2 border rounded text-sm"
            onChange={() => {}}
          />
        </div>
      </td>
      <td className="p-3">
        <div className="max-w-xs">
          <textarea
            value={step.ttsText}
            className="w-full p-2 border rounded text-sm resize-none"
            rows={2}
            onChange={() => {}}
          />
        </div>
      </td>
      <td className="p-3">
        <div className="max-w-xs">
          <input
            type="text"
            value={step.visualSuggestion}
            className="w-full p-2 border rounded text-sm"
            onChange={() => {}}
          />
        </div>
      </td>
      <td className="p-3">
        <div className="text-sm">
          <div className="mb-1">
            <span className="font-medium">Doc:</span> {step.sourceDocReference.section}
          </div>
          <div className="text-xs text-muted-foreground">
            Page {step.sourceDocReference.page} • {Math.round(step.sourceDocReference.confidence * 100)}%
          </div>
        </div>
      </td>
      <td className="p-3">
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            className="h-8 w-8 p-0"
          >
            <Edit3 className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDuplicate}
            className="h-8 w-8 p-0"
          >
            <Copy className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </td>
    </tr>
  );
};

export const StoryboardEditor: React.FC<StoryboardEditorProps> = ({
  storyboard,
  onStoryboardUpdate,
  onStepEdit,
  isLoading = false
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('simple');

  const handleStepEdit = useCallback((stepId: string) => {
    onStepEdit(stepId);
  }, [onStepEdit]);

  const handleStepDelete = useCallback((stepId: string) => {
    const updatedSteps = storyboard.steps.filter(step => step.id !== stepId);
    const reorderedSteps = updatedSteps.map((step, index) => ({
      ...step,
      order: index + 1
    }));
    
    onStoryboardUpdate({
      ...storyboard,
      steps: reorderedSteps
    });
  }, [storyboard, onStoryboardUpdate]);

  const handleStepDuplicate = useCallback((stepId: string) => {
    const stepIndex = storyboard.steps.findIndex(step => step.id === stepId);
    const stepToDuplicate = storyboard.steps[stepIndex];
    
    const newStep = {
      ...stepToDuplicate,
      id: `step_${Date.now()}`,
      order: stepToDuplicate.order + 1,
      onscreenText: `${stepToDuplicate.onscreenText} (Copy)`
    };

    const updatedSteps = [
      ...storyboard.steps.slice(0, stepIndex + 1),
      newStep,
      ...storyboard.steps.slice(stepIndex + 1)
    ].map((step, index) => ({
      ...step,
      order: index + 1
    }));

    onStoryboardUpdate({
      ...storyboard,
      steps: updatedSteps
    });
  }, [storyboard, onStoryboardUpdate]);

  const handleStepReorder = useCallback((stepId: string, direction: 'up' | 'down') => {
    const stepIndex = storyboard.steps.findIndex(step => step.id === stepId);
    const newIndex = direction === 'up' ? stepIndex - 1 : stepIndex + 1;
    
    if (newIndex < 0 || newIndex >= storyboard.steps.length) return;

    const updatedSteps = [...storyboard.steps];
    [updatedSteps[stepIndex], updatedSteps[newIndex]] = [updatedSteps[newIndex], updatedSteps[stepIndex]];
    
    const reorderedSteps = updatedSteps.map((step, index) => ({
      ...step,
      order: index + 1
    }));

    onStoryboardUpdate({
      ...storyboard,
      steps: reorderedSteps
    });
  }, [storyboard, onStoryboardUpdate]);

  const handleAddStep = useCallback(() => {
    const newStep: StoryboardStep = {
      id: `step_${Date.now()}`,
      order: storyboard.steps.length + 1,
      onscreenText: 'New step',
      ttsText: 'New step description',
      visualSuggestion: 'Visual suggestion for new step',
      sourceDocReference: {
        docId: storyboard.documentSource.id,
        page: 1,
        section: 'Manual',
        confidence: 0.5
      },
      validation: {
        completeness: 'medium',
        safety: 'verified',
        clarity: 'good'
      },
      changeHistory: []
    };

    onStoryboardUpdate({
      ...storyboard,
      steps: [...storyboard.steps, newStep]
    });
  }, [storyboard, onStoryboardUpdate]);

  const toggleViewMode = () => {
    setViewMode(current => current === 'simple' ? 'advanced' : 'simple');
  };

  const getValidationStatus = () => {
    const totalSteps = storyboard.steps.length;
    const validatedSteps = storyboard.steps.filter(step => 
      step.validation.completeness === 'high' && 
      step.validation.safety === 'verified'
    ).length;
    
    return {
      percentage: Math.round((validatedSteps / totalSteps) * 100),
      status: validatedSteps === totalSteps ? 'passed' : 'warning'
    };
  };

  const validationStatus = getValidationStatus();

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Generating Storyboard...</CardTitle>
          <CardDescription>
            Creating your step-by-step guide
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              {storyboard.headline}
              <span className="text-sm font-normal text-muted-foreground">
                ({storyboard.templateType})
              </span>
            </CardTitle>
            <CardDescription>
              {storyboard.steps.length} steps • {storyboard.documentSource.title}
            </CardDescription>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Validation Status */}
            <div className="flex items-center gap-2">
              {validationStatus.status === 'passed' ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-yellow-600" />
              )}
              <span className="text-sm text-muted-foreground">
                {validationStatus.percentage}% validated
              </span>
            </div>
            
            {/* View Mode Toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={toggleViewMode}
              className="flex items-center gap-2"
            >
              {viewMode === 'simple' ? (
                <>
                  <Table className="h-4 w-4" />
                  Advanced
                </>
              ) : (
                <>
                  <List className="h-4 w-4" />
                  Simple
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-4">
          {/* Add Step Button */}
          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddStep}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Step
            </Button>
            
            {/* TODO: implement save workflow (dirty state) */}
          </div>

          {/* Steps Content */}
          {viewMode === 'simple' ? (
            <div className="space-y-3">
              {storyboard.steps.map((step, index) => (
                <StepCard
                  key={step.id}
                  step={step}
                  index={index}
                  isSimpleMode={true}
                  onEdit={() => handleStepEdit(step.id)}
                  onDelete={() => handleStepDelete(step.id)}
                  onDuplicate={() => handleStepDuplicate(step.id)}
                  onReorder={(direction) => handleStepReorder(step.id, direction)}
                />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2">
                    <th className="p-3 text-left">Step #</th>
                    <th className="p-3 text-left">Onscreen Text</th>
                    <th className="p-3 text-left">TTS Text</th>
                    <th className="p-3 text-left">Visual Suggestion</th>
                    <th className="p-3 text-left">Source Reference</th>
                    <th className="p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {storyboard.steps.map((step, index) => (
                    <StepCard
                      key={step.id}
                      step={step}
                      index={index}
                      isSimpleMode={false}
                      onEdit={() => handleStepEdit(step.id)}
                      onDelete={() => handleStepDelete(step.id)}
                      onDuplicate={() => handleStepDuplicate(step.id)}
                      onReorder={(direction) => handleStepReorder(step.id, direction)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};